import { Injectable, OnDestroy, signal } from '@angular/core';

/** Held (exclusively, for the life of the window) by the detached chat window. The browser frees it if that window dies. */
export const DETACH_LOCK_NAME = 'rfi-chat-owner';

export const DETACH_CHANNEL_NAME = 'rfi-chat-detach';

/** Written every 2 s by the pop-out window (localStorage is shared); the fallback when Web Locks are missing. */
export const POPOUT_ALIVE_KEY = 'rfi-chat-popout-alive';
const POPOUT_ALIVE_MS = 6000;

/** Wait before a second try at the lock: a tab may hold it for an instant while it probes. */
const LOCK_RETRY_MS = 150;

/** Cookie the detached window keeps fresh; read by every page on the site, V1's chat frames included. */
export const OWNER_COOKIE = 'rfi_chat_detached';
/** Longer than the 2 s heartbeat with room for a throttled timer; a window that died without telling is forgotten after it. */
const OWNER_COOKIE_FRESH_MS = 15000;

function cookieDomain(host: string): string | null {
  const labels = host.split('.');
  if (labels.length < 2 || /^[\d.]+$/.test(host)) return null; // localhost / an IP: a host-only cookie is shared across ports
  return `.${labels.slice(-2).join('.')}`;
}

function setOwnerCookie(value: string, maxAge: number): void {
  try {
    const domain = cookieDomain(window.location.hostname);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${OWNER_COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${secure}`;
  } catch {
    /* cookies blocked: the lock still covers this origin */
  }
}

function writeOwnerCookie(): void {
  setOwnerCookie(String(Date.now()), Math.ceil(OWNER_COOKIE_FRESH_MS / 1000));
}

function clearOwnerCookie(): void {
  setOwnerCookie('', 0);
}

function ownerCookieFresh(): boolean {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${OWNER_COOKIE}=(\\d+)`));
    return !!m && Date.now() - Number(m[1]) < OWNER_COOKIE_FRESH_MS;
  } catch {
    return false;
  }
}

export interface DetachThreadRequest {
  /** Empty / null: just bring the window forward. */
  jobKey: string | null;
  vendorKey: string | null;
}

type DetachMessage = { t: 'detached' } | { t: 'open-thread'; jobKey?: string | null; vendorKey?: string | null };

/**
 * One detached job chat window per browser profile, owned by nobody but itself (Nahid, 1 Nov).
 *
 * The detached window ("pop-out", route /chat-window) takes a Web Lock and keeps it until it closes. Every other
 * window of the app (V2 tabs and V1's chat frames) only ever *asks* whether the lock is held: if it is, they hide
 * their bubble ({@link detachedElsewhere}); the browser tells them the moment the window is gone, so there is no
 * heartbeat and no polling. Tabs never keep the lock. `BroadcastChannel` carries two tiny messages: `detached`
 * (the window just opened) and `open-thread` (a tab asks the window to show a job's chat).
 *
 * Without Web Locks the pop-out's existing heartbeat (POPOUT_ALIVE_KEY) is read instead.
 */
@Injectable({ providedIn: 'root' })
export class ChatDetachService implements OnDestroy {
  /** This window is the detached chat window (route /chat-window). */
  readonly isDetachedWindow =
    typeof window !== 'undefined' && window.location.pathname.toLowerCase().startsWith('/chat-window');

  /** A detached chat window exists in this browser profile and this window is not it. */
  readonly detachedElsewhere = signal(false);

  private channel: BroadcastChannel | null = null;
  private started = false;
  private watching = false;
  private rewatch = false;
  private openThreadListener: ((r: DetachThreadRequest) => void) | null = null;
  /** What the lock / heartbeat says, and what the cookie says: either one means a detached window exists. */
  private lockSays = false;
  private cookieSays = false;
  private cookieStoreHandler: ((e: Event) => void) | null = null;
  private readonly onStorage = (e: StorageEvent) => {
    if (e.key === POPOUT_ALIVE_KEY || e.key === null) this.setLockSays(this.popoutAlive());
  };
  private readonly onWake = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') this.refreshCookie();
  };

  private setLockSays(v: boolean): void {
    this.lockSays = v;
    this.detachedElsewhere.set(this.lockSays || this.cookieSays);
  }

  private refreshCookie(): void {
    this.cookieSays = ownerCookieFresh();
    this.detachedElsewhere.set(this.lockSays || this.cookieSays);
  }

  /**
   * V1's chat frame runs on V1's own origin, so it cannot see this origin's lock, channel or storage. A cookie on the
   * shared site is the one thing both origins can read, and `cookieStore` tells them when it changes (no polling);
   * where that API is missing it is read again whenever the page is shown or focused.
   */
  private watchOwnerCookie(): void {
    this.refreshCookie();
    const store = (window as unknown as { cookieStore?: EventTarget }).cookieStore;
    if (store) {
      this.cookieStoreHandler = () => this.refreshCookie();
      store.addEventListener('change', this.cookieStoreHandler);
    }
    document.addEventListener('visibilitychange', this.onWake);
    window.addEventListener('focus', this.onWake);
  }

  /** The detached window: say "I am here" (called by its heartbeat), and take it back when the window goes. */
  touchOwnerCookie(): void {
    if (this.owner) writeOwnerCookie();
  }

  /**
   * The tab that opened a Picture-in-Picture window speaks for it too, for as long as it is open: that tab is a normal
   * top-level page (the same storage as every other tab), and it outlives the window, so the cookie is kept fresh and
   * every page hears about the window even if the iframe inside it could not.
   */
  announcePipWindow(pip: Window): void {
    writeOwnerCookie();
    this.refreshCookie();
    this.post({ t: 'detached' });
    const beat = setInterval(writeOwnerCookie, 2000);
    pip.addEventListener(
      'pagehide',
      () => {
        clearInterval(beat);
        clearOwnerCookie();
        this.refreshCookie();
      },
      { once: true },
    );
  }

  /** This window got the lock: it is the one detached chat window. */
  private owner = false;
  private readonly onOwnerGone = () => {
    if (this.owner) clearOwnerCookie();
  };

  /** Starts watching (or, in the detached window, owning). Safe to call more than once. */
  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel = new BroadcastChannel(DETACH_CHANNEL_NAME);
        this.channel.onmessage = (e: MessageEvent) => this.onMessage(e.data);
      }
    } catch {
      this.channel = null;
    }
    if (this.isDetachedWindow) {
      void this.takeOwnerLock();
      window.addEventListener('pagehide', this.onOwnerGone);
    } else {
      this.watchOwnerCookie();
      if (this.hasLocks()) {
        void this.watchOwnerLock();
      } else {
        this.setLockSays(this.popoutAlive());
        window.addEventListener('storage', this.onStorage);
      }
    }
  }

  ngOnDestroy(): void {
    this.channel?.close();
    this.channel = null;
    if (typeof window === 'undefined') return;
    window.removeEventListener('storage', this.onStorage);
    window.removeEventListener('pagehide', this.onOwnerGone);
    window.removeEventListener('focus', this.onWake);
    document.removeEventListener('visibilitychange', this.onWake);
    if (this.cookieStoreHandler) {
      (window as unknown as { cookieStore?: EventTarget }).cookieStore?.removeEventListener('change', this.cookieStoreHandler);
    }
  }

  /** The detached window asks to hear about `open-thread` requests from other windows. Returns the unsubscribe. */
  setOpenThreadListener(listener: (r: DetachThreadRequest) => void): () => void {
    this.openThreadListener = listener;
    return () => {
      if (this.openThreadListener === listener) this.openThreadListener = null;
    };
  }

  /**
   * Another window wants a thread of the chat opened. When a detached window exists the request goes to it and this
   * returns true (the caller must not open the chat here); otherwise false.
   */
  requestOpenThread(jobKey: string | null, vendorKey: string | null): boolean {
    if (this.isDetachedWindow || !this.detachedElsewhere() || !this.channel) return false;
    this.post({ t: 'open-thread', jobKey, vendorKey });
    return true;
  }

  private hasLocks(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
  }

  private popoutAlive(): boolean {
    try {
      const at = Number(localStorage.getItem(POPOUT_ALIVE_KEY));
      return Number.isFinite(at) && at > 0 && Date.now() - at < POPOUT_ALIVE_MS;
    } catch {
      return false;
    }
  }

  private post(m: DetachMessage): void {
    try {
      this.channel?.postMessage(m);
    } catch {
      /* channel closed */
    }
  }

  private onMessage(data: unknown): void {
    const m = data as DetachMessage | null;
    if (!m || typeof m !== 'object') return;
    if (m.t === 'detached' && !this.isDetachedWindow) {
      this.setLockSays(true);
      if (!this.hasLocks()) return;
      if (this.watching) this.rewatch = true;
      else void this.watchOwnerLock();
    } else if (m.t === 'open-thread' && this.isDetachedWindow) {
      this.openThreadListener?.({ jobKey: m.jobKey?.trim() || null, vendorKey: m.vendorKey?.trim() || null });
    }
  }

  // ---- The detached window: hold the lock for life --------------------------------------------------------------

  private async takeOwnerLock(): Promise<void> {
    if (!this.hasLocks()) {
      this.owner = true;
      writeOwnerCookie();
      this.post({ t: 'detached' }); // the pop-out heartbeat does the rest
      return;
    }
    let owned = await this.tryOwnerLock();
    if (!owned) {
      await new Promise<void>((resolve) => setTimeout(resolve, LOCK_RETRY_MS));
      owned = await this.tryOwnerLock();
    }
    if (owned) {
      this.owner = true;
      writeOwnerCookie();
    }
    // Still not ours: another detached window already owns it. Hand this one's thread over and leave, so there is
    // exactly one (a tab opened independently can't find the existing window by name).
    if (!owned) this.handOver();
  }

  private tryOwnerLock(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      navigator.locks
        .request(DETACH_LOCK_NAME, { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false);
            return undefined;
          }
          this.post({ t: 'detached' });
          resolve(true);
          return new Promise<void>(() => undefined); // never settles: held until the window is gone
        })
        .catch(() => resolve(false));
    });
  }

  private handOver(): void {
    const q = new URLSearchParams(window.location.search);
    this.post({ t: 'open-thread', jobKey: q.get('job'), vendorKey: q.get('vendor') });
    // In a Picture-in-Picture window this page is an iframe: the PiP window is its top.
    try {
      if (window.top && window.top !== window) {
        window.top.close();
        return;
      }
    } catch {
      /* cross-origin top */
    }
    window.close();
  }

  // ---- Every other window: ask, and wait to be told ----------------------------------------------------------------

  private async watchOwnerLock(): Promise<void> {
    if (this.watching) return;
    this.watching = true;
    try {
      do {
        this.rewatch = false;
        const free = await navigator.locks.request(DETACH_LOCK_NAME, { mode: 'shared', ifAvailable: true }, (lock) => !!lock);
        this.setLockSays(!free);
        if (!free) {
          // Queued: the browser grants it the moment the detached window is gone. Nothing runs while we wait.
          await navigator.locks.request(DETACH_LOCK_NAME, { mode: 'shared' }, () => undefined);
          this.setLockSays(false);
        }
      } while (this.rewatch);
    } catch {
      this.setLockSays(false);
    } finally {
      this.watching = false;
    }
  }
}
